import { AddressItem } from "./modals";
import { getServiceCategoryFromCategoryName, SERVICE_CATEGORY } from "./serviceCategory";
import { ADDRESS_TYPE_LABEL } from "./userType";

/**
 * ADDRESS_TYPE.HOME is 0, so Number("") must not be treated as a selection.
 * Returns a type only after the user picks Home, Work, or Other.
 */
export const parseSelectedAddressType = (
  value: unknown,
): number | undefined => {
  if (value === "" || value == null) return undefined;
  const num = Number(value);
  if (!Number.isInteger(num) || !(num in ADDRESS_TYPE_LABEL)) return undefined;
  return num;
};

export const formatAddressTypeLabel = (value: unknown): string => {
  const addressType = parseSelectedAddressType(value);
  if (addressType === undefined) return "";
  return ADDRESS_TYPE_LABEL[addressType as keyof typeof ADDRESS_TYPE_LABEL] || "";
};

export const formatRegistrationNumber = (registrationNumber: string) => {
  // Regex pattern to match the Indian vehicle registration number
  const regex = /^([A-Z]{2})(\d{2})([A-Z]{1,3})(\d{4})$/;

  // Replace using regex with formatted output
  return registrationNumber.replace(regex, "$1 $2 $3 $4");
};

/** Parse a Google Places result into address form fields. */
export const addressFormatter = (place: any) => {
  const components: any[] = place?.address_components || [];
  const getComponent = (type: string, useShort = false) => {
    const match = components.find((c) => c?.types?.includes(type));
    if (!match) return "";
    return useShort ? match.short_name || "" : match.long_name || "";
  };

  const lat =
    typeof place?.geometry?.location?.lat === "function"
      ? place.geometry.location.lat()
      : place?.geometry?.location?.lat;
  const lng =
    typeof place?.geometry?.location?.lng === "function"
      ? place.geometry.location.lng()
      : place?.geometry?.location?.lng;

  const sublocality =
    getComponent("sublocality_level_1") ||
    getComponent("sublocality") ||
    getComponent("neighborhood") ||
    getComponent("premise");

  const streetNumber = getComponent("street_number");
  const route = getComponent("route");
  const streetLine = [streetNumber, route].filter(Boolean).join(" ").trim();

  return {
    addressLine1:
      place?.name ||
      streetLine ||
      place?.formatted_address?.split(",")[0] ||
      "",
    addressLine2: sublocality,
    city:
      getComponent("locality") ||
      getComponent("administrative_area_level_2") ||
      "",
    state: getComponent("administrative_area_level_1") || "",
    pincode: getComponent("postal_code") || "",
    location: {
      type: "Point",
      coordinates: [
        lng != null ? String(lng) : "",
        lat != null ? String(lat) : "",
      ],
    },
    formattedAddress: place?.formatted_address || "",
  };
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const getTodayDate = () => {
  return new Date();
};

export const calculateDiscountedPrice = (
  originalPrice: number,
  discountPercentage: number,
) => {
  if (originalPrice < 0 || discountPercentage < 0) return;

  const discountAmount = Math.round((discountPercentage / 100) * originalPrice);
  const discountedPrice = Math.round(originalPrice - discountAmount);

  return {
    discountAmount,
    discountedPrice,
  };
};

const LOCAL_PREVIEW_URL_PREFIX = "blob:";

export const isLocalPreviewUrl = (url?: string) =>
  Boolean(url && url.startsWith(LOCAL_PREVIEW_URL_PREFIX));

type MutableRef<T> = { current: T };

export type CoverImageState = {
  imageUrl: string;
  titleImageKeyRef: MutableRef<string>;
  titleFileRef: MutableRef<File | null>;
  fileRef: MutableRef<HTMLInputElement | null>;
  setImageUrl: (url: string) => void;
};

type ResetFormMediaItem = {
  image?: string;
  previewUrl?: string;
};

type ResetFormValues = {
  tools?: ResetFormMediaItem[];
  requirementFromUser?: ResetFormMediaItem[];
  category?: unknown;
  title?: string;
};

export const clearCoverImage = ({
  imageUrl,
  titleImageKeyRef,
  titleFileRef,
  fileRef,
  setImageUrl,
}: CoverImageState) => {
  if (isLocalPreviewUrl(imageUrl)) URL.revokeObjectURL(imageUrl);
  titleImageKeyRef.current = "";
  titleFileRef.current = null;
  setImageUrl("");
  if (fileRef.current) fileRef.current.value = "";
};

export const resetFormData = (
  values: ResetFormValues,
  coverImage: CoverImageState,
  bumpFormResetKey: () => void,
) => {
  [...(values.tools || []), ...(values.requirementFromUser || [])].forEach(
    (item) => {
      if (item?.image && isLocalPreviewUrl(item.image)) {
        URL.revokeObjectURL(item.image);
      }
      if (item?.previewUrl && isLocalPreviewUrl(item.previewUrl)) {
        URL.revokeObjectURL(item.previewUrl);
      }
    },
  );
  clearCoverImage(coverImage);
  bumpFormResetKey();
};

export const displayOrNA = (value?: string | number | null) => {
  if (value === undefined || value === null) return "N/A";
  const text = String(value).trim();
  return text ? text : "N/A";
};

export const formatFileSize = (bytes?: number) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

export const isHttpUrl = (value?: string) =>
  Boolean(value && /^https?:\/\//i.test(value.trim()));

export const hasAwsSignedQuery = (url?: string) =>
  /[?&]X-Amz-(Signature|Algorithm)=/i.test(`${url || ""}`);

/** Stored S3 object URL/key, not a display title. */
export const isStoredMediaUrl = (value?: string) => {
  const text = `${value || ""}`.trim();
  if (!text || isLocalPreviewUrl(text) || text.startsWith("data:")) return false;
  if (isHttpUrl(text) || text.includes("/")) return true;
  return /\.(jpe?g|png|webp|gif|bmp|mp4|webm|ogg|mov)$/i.test(text);
};

export const titleFromStoredFile = (value?: string) => {
  const text = `${value || ""}`.trim();
  if (!text) return "";
  try {
    const path = decodeURIComponent(new URL(text).pathname);
    const fileName = path.split("/").filter(Boolean).pop() || "";
    return fileName.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
  } catch {
    const fileName = text.split("/").filter(Boolean).pop() || text;
    return fileName.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
  }
};

/** S3/object UUID-style names should never be shown as titles. */
export const isGeneratedMediaLabel = (value?: string) => {
  const text = `${value || ""}`.trim();
  if (!text) return true;
  const compact = text.replace(/[\s_-]/g, "").replace(/\.[^.]+$/, "");
  if (/^[0-9a-f]{32}$/i.test(compact)) return true;
  if (/^[0-9a-f]{24}$/i.test(compact)) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    text.replace(/\s+/g, "-").replace(/\.[^.]+$/, ""),
  );
};

export const toDisplayTitle = (value?: string) => {
  const text = `${value || ""}`.trim();
  if (!text || isStoredMediaUrl(text) || isGeneratedMediaLabel(text)) return "";
  return text;
};

export const displayFileLabel = (fileName?: string, fallback = "Uploaded file") => {
  const name = `${fileName || ""}`.trim();
  if (!name || isGeneratedMediaLabel(name)) return fallback;
  return name;
};

export const formatAddressLabel = (addr: AddressItem) => {
  const parts = [
    addr.firstName,
    addr.addressLine1,
    addr.addressLine2,
    addr.city,
    addr.state,
    addr.pincode,
  ].filter(Boolean);
  return parts.join(", ");
};



export const defaultEditPermissions = {
  hasAssignedMerchant: false,
  /** True when service date is tomorrow or a later calendar day. */
  isScheduleOnNextDayOrLater: false,
  /** @deprecated Use isScheduleOnNextDayOrLater */
  isScheduleBeyond24Hours: false,
  canEditMerchant: false,
  canEditOrderDetails: false,
  /** True when orderStatus is cancelled by service partner (7) — form is read-only. */
  isFullyLocked: false,
};

export const isVehicleCategory = (categoryName: string) => {
  const category = getServiceCategoryFromCategoryName(categoryName);
  return (
    category === SERVICE_CATEGORY.CAR || category === SERVICE_CATEGORY.BIKE
  );
};